import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, FormattedMessage } from 'react-intl';
import uuid from 'uuid/v4';
import get from 'lodash.get';
import { Grid, Typography, Button } from '@material-ui/core';

import { LOCATION_DEFAULT } from 'yeself-common/defaults/NodeDefaults';
import {
    GUIDANCE_CONTROL_ATTACHMENT,
    GUIDANCE_CONTROL_PERSISTENCE,
} from 'yeself-common/typings/application/ApplicationEnum';
import { FEATURE_FLAG_NAMES } from 'yeself-common/typings/FeatureFlags';

import LocationPathsForm from 'yeself-client-common/form/components/LocationPathsForm';
import LocalizedPlainStringField from 'yeself-client-common/form/components/LocalizedPlainStringField';
import SwitchButton from 'yeself-client-common/form/components/SwitchButton';
import GuidanceControlAttachmentForm from 'yeself-client-common/form/components/GuidanceControlAttachmentForm';
import GuidanceControlPersistence from 'yeself-client-common/form/components/GuidanceControlPersistence';
import Feature from 'yeself-client-common/featureFlags/components/Feature';

import { updateDetailSettings } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectApplicationMetaData,
    selectContextHelpContent,
    selectFallbackLanguage,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormItemSelect from 'Console/components/common/form/FormItemSelect';

import { CONTEXT_HELP_LAUNCHER_POSITION } from 'Console/constants/appConstants';
import { selectContentLanguageCode, selectContentLanguage } from 'Console/data/app/selectors';

import ViewPlaylistsField from './components/ViewPlaylistsField';

const styles = (theme) => ({
    root: {
        flexGrow: 1,
        padding: '0 24px',
        fontSize: theme.typography.pxToRem(14),
        width: '80%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
        },
    },
    title: {
        display: 'inline-block',
        paddingBottom: theme.typography.pxToRem(20),
    },
    btn: {
        marginTop: theme.typography.pxToRem(20),
        marginBottom: theme.typography.pxToRem(40),
    },
    formInput: {
        width: '65%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginBottom: '5%',
        },
    },
    formName: {
        width: '30%',
        marginRight: '5%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginRight: 0,
            paddingBottom: theme.typography.pxToRem(10),
        },
    },
    itemsBox: {
        paddingBottom: theme.typography.pxToRem(16),
    },
    textField: {
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginBottom: '5%',
        },
        marginTop: 0,
        marginBottom: 0,
        background: '#FAFAFA',
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12)',
        borderRadius: 2,
    },
    menu: {
        width: 250,
    },
    cssSelect: {
        '&:focus': {
            background: 'inherit',
        },
    },
    cssSelectRoot: {
        backgroundColor: 'inherit',
        borderRadius: 'inherit',
        '&:hover': {
            backgroundColor: 'inherit',
            borderBottom: 'none',
        },
        '&$focused': {
            backgroundColor: 'inherit',
        },
        '&$disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
        },
    },
    cssUnderline: {
        '&:before': {
            borderBottom: 'none',
        },
        '&:after': {
            borderBottom: 'none',
        },
        '&:hover:not([disabled]):not([focused]):not([error]):before': {
            borderBottom: `inherit !important`,
        },
    },
});

const Settings = ({
    classes,
    intl: { formatMessage },
    form,
    form: { getFieldDecorator, getFieldValue },
}) => {
    const dispatch = useDispatch();

    const metadata = useSelector(selectApplicationMetaData);
    const content = useSelector(selectContextHelpContent);
    const contentLanguageCode = useSelector(selectContentLanguageCode);
    const contentLanguage = useSelector(selectContentLanguage);
    const fallbackLanguage = useSelector(selectFallbackLanguage);

    const handleSaveContextHelpSettings = useCallback(() => {
        const {
            tourListHeading,
            playlistListHeading,
            journeyListHeading,
            restartQuestion,
            confirmRestart,
            declineRestart,
            type,
            searchPlaceHolder,
            noResultsFound,
            textNotification,
            listOfArticlesHeading,
            learnMoreOfArticle,
            recommendedTourListHeading,
            content,
            suppressVisibleGuides,
            container,
            viewPlaylistsText,
        } = form.getFieldValue('contextHelp');

        const locationInData = {
            ...get(metadata, 'contextHelp.container.location', ''),
            id: uuid(),
        };

        const newLocationData = container ? { ...container.location, id: uuid() } : locationInData;

        dispatch(
            updateDetailSettings(
                {
                    'metadata.contextHelp.content.listOfToursHeading': tourListHeading,
                    'metadata.contextHelp.content.listOfPlaylistsHeading': playlistListHeading,
                    'metadata.contextHelp.content.journeyTrackersHeading': journeyListHeading,
                    'metadata.contextHelp.content.restartProgressQuestion': restartQuestion,
                    'metadata.contextHelp.content.restartProgressQuestionYes': confirmRestart,
                    'metadata.contextHelp.content.restartProgressQuestionNo': declineRestart,
                    'metadata.contextHelp.container.location': newLocationData,
                    'metadata.contextHelp.container.type': type,
                    'metadata.contextHelp.content.searchInputPlaceHolder': searchPlaceHolder,
                    'metadata.contextHelp.content.noResultsFound': noResultsFound,
                    'metadata.contextHelp.content.notification': {
                        ...metadata.contextHelp.content.notification,
                        ...content.notification,
                        notificationText: textNotification,
                    },
                    'metadata.contextHelp.content.guidanceControl': {
                        ...metadata.contextHelp.content.guidanceControl,
                        ...content.guidanceControl,
                    },
                    'metadata.contextHelp.content.listOfArticlesHeading': listOfArticlesHeading,
                    'metadata.contextHelp.content.learnMoreOfArticle': learnMoreOfArticle,
                    'metadata.contextHelp.content.recommendedJourneysHeading': recommendedTourListHeading,
                    'metadata.contextHelp.suppressVisibleGuides': suppressVisibleGuides,
                    'metadata.contextHelp.content.viewPlaylistsText': viewPlaylistsText || {},
                },
                {
                    success: (
                        <FormattedMessage
                            id="Notification.save"
                            defaultMessage="Your settings were saved"
                        />
                    ),

                    fail: (
                        <FormattedMessage
                            id="Notification.fail"
                            defaultMessage="Unable to save, something went wrong"
                        />
                    ),
                }
            )
        );
    }, [form, metadata, dispatch]);

    const locationData = get(metadata, 'contextHelp.container.location', LOCATION_DEFAULT);
    const persistanceOption = get(
        metadata,
        'contextHelp.content.guidanceControl.browserPersistence',
        GUIDANCE_CONTROL_PERSISTENCE.BROWSER_RESTART
    );
    const attachmentOption = get(
        metadata,
        'contextHelp.content.guidanceControl.flags.switchVisualGuidance.attachment',
        GUIDANCE_CONTROL_ATTACHMENT.CONTEXT_HELP
    );
    const guidanceControlLabelOn = get(
        metadata,
        'contextHelp.content.guidanceControl.flags.switchVisualGuidance.labelOn',
        {}
    );
    const guidanceControlLabelOff = get(
        metadata,
        'contextHelp.content.guidanceControl.flags.switchVisualGuidance.labelOff',
        {}
    );

    getFieldDecorator('contextHelp.type', {
        initialValue: get(metadata, 'contextHelp.container.type', 'themePositioned'),
        preserve: true,
    });

    return (
        <Grid container className={classes.root}>
            <Feature name={FEATURE_FLAG_NAMES.TOUR}>
                <Grid container className={classes.itemsBox}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.title}>
                            <FormattedMessage id="ContextHelp.tours" defaultMessage="Tours" />
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.listHeading"
                                defaultMessage="List Heading"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            {getFieldDecorator('contextHelp.tourListHeading', {
                                initialValue: content.listOfToursHeading,
                                preserve: true,
                            })(
                                <LocalizedPlainStringField
                                    locale={contentLanguage}
                                    fallbackLocale={fallbackLanguage.value}
                                    placeholder={formatMessage({
                                        id: 'ContextHelp.listOfTours.placeholder',
                                        defaultMessage: 'List of Tours',
                                    })}
                                />
                            )}
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.suppressVisibleGuides"
                                defaultMessage="Suppress visible tours when started from Context Help or Journey Tracker"
                            />
                        </Typography>
                        <Grid id="yeself-switch-wrapper" item>
                            {getFieldDecorator('contextHelp.suppressVisibleGuides', {
                                initialValue: metadata.contextHelp?.suppressVisibleGuides || false,
                                preserve: true,
                            })(
                                <span id="yeself-switch-button">
                                    <SwitchButton
                                        color="primary"
                                        value={getFieldValue('contextHelp.suppressVisibleGuides')}
                                    />
                                </span>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Feature>
            <Feature name={FEATURE_FLAG_NAMES.PLAYLISTS}>
                <Grid container className={classes.itemsBox}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.title}>
                            <FormattedMessage id="ContextHelp.playlists" defaultMessage="Playlists" />
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.listHeading"
                                defaultMessage="List Heading"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            {getFieldDecorator('contextHelp.playlistListHeading', {
                                initialValue: content.listOfPlaylistsHeading,
                                preserve: true,
                            })(
                                <LocalizedPlainStringField
                                    locale={contentLanguage}
                                    fallbackLocale={fallbackLanguage.value}
                                    placeholder={formatMessage({
                                        id: 'ContextHelp.playlistListHeading.placeholder',
                                        defaultMessage: 'List of Playlists',
                                    })}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Feature>

            <Feature name={FEATURE_FLAG_NAMES.JOURNEYS}>
                <Grid container className={classes.itemsBox}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.title}>
                            <FormattedMessage id="ContextHelp.journeys" defaultMessage="Journeys" />
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.listHeading"
                                defaultMessage="List Heading"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            {getFieldDecorator('contextHelp.journeyListHeading', {
                                initialValue: content.journeyTrackersHeading,
                                preserve: true,
                            })(
                                <LocalizedPlainStringField
                                    locale={contentLanguage}
                                    fallbackLocale={fallbackLanguage.value}
                                    placeholder={formatMessage({
                                        id: 'ContextHelp.journeyListHeading.placeholder',
                                        defaultMessage: 'List of Journeys',
                                    })}
                                />
                            )}
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.enable.notification"
                                defaultMessage="Enable success notification for finished tour"
                            />
                        </Typography>
                        <Grid id="yeself-switch-button" item>
                            {getFieldDecorator('contextHelp.content.notification.enable', {
                                initialValue: content.notification.enable || false,
                                preserve: true,
                            })(
                                <span id="yeself-switch-button">
                                    <SwitchButton
                                        color="primary"
                                        value={getFieldValue('contextHelp.content.notification.enable')}
                                    />
                                </span>
                            )}
                        </Grid>
                    </Grid>
                    {getFieldValue('contextHelp.content.notification.enable') ? (
                        <Grid
                            container
                            direction="row"
                            alignItems="center"
                            style={{ paddingBottom: '20px' }}
                        >
                            <Typography variant="body1" className={classes.formName}>
                                <FormattedMessage
                                    id="ContextHelp.notification.text"
                                    defaultMessage="Notification text"
                                />
                            </Typography>

                            <Grid item className={classes.formInput}>
                                {getFieldDecorator('contextHelp.textNotification', {
                                    initialValue: content.notification.notificationText,
                                    preserve: true,
                                })(
                                    <LocalizedPlainStringField
                                        locale={contentLanguage}
                                        fallbackLocale={fallbackLanguage.value}
                                        placeholder={formatMessage({
                                            id: 'ContextHelp.textNotification.placeholder',
                                            defaultMessage:
                                                'You have successfully finished the tour. Please continue further in the journey.',
                                        })}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    ) : null}
                </Grid>
            </Feature>

            <Feature name={FEATURE_FLAG_NAMES.RECOMMENDATION}>
                <Grid container className={classes.itemsBox}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.title}>
                            <FormattedMessage
                                id="ContextHelp.recommendedTours"
                                defaultMessage="Recommended tours"
                            />
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.listHeading"
                                defaultMessage="List Heading"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            {getFieldDecorator('contextHelp.recommendedTourListHeading', {
                                initialValue: content.recommendedJourneysHeading,
                                preserve: true,
                            })(
                                <LocalizedPlainStringField
                                    locale={contentLanguage}
                                    fallbackLocale={fallbackLanguage.value}
                                    placeholder={formatMessage({
                                        id: 'ContextHelp.listOfRecommendedTours.placeholder',
                                        defaultMessage: 'List of Recommended tours',
                                    })}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Feature>

            <Grid container className={classes.itemsBox}>
                <Feature name={FEATURE_FLAG_NAMES.PROGRESS_TRACKER}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.title}>
                            <FormattedMessage
                                id="ContextHelp.progressTracker"
                                defaultMessage="Progress tracker"
                            />
                        </Typography>
                    </Grid>

                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.restartQuestion"
                                defaultMessage="Restart question"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            {getFieldDecorator('contextHelp.restartQuestion', {
                                initialValue: content.restartProgressQuestion,
                                preserve: true,
                            })(
                                <LocalizedPlainStringField
                                    locale={contentLanguage}
                                    fallbackLocale={fallbackLanguage.value}
                                    placeholder={formatMessage({
                                        id: 'ContextHelp.restartQuestion.placeholder',
                                        defaultMessage: 'Are you sure you want to restart the progress?',
                                    })}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Feature>

                <Grid container direction="row" alignItems="center" style={{ paddingBottom: '20px' }}>
                    <Typography variant="body1" className={classes.formName}>
                        <FormattedMessage
                            id="ContextHelp.confirmRestart"
                            defaultMessage="Confirm restart"
                        />
                    </Typography>

                    <Grid className={classes.formInput}>
                        {getFieldDecorator('contextHelp.confirmRestart', {
                            initialValue: content.restartProgressQuestionYes,
                            preserve: true,
                        })(
                            <LocalizedPlainStringField
                                locale={contentLanguage}
                                fallbackLocale={fallbackLanguage.value}
                                placeholder={formatMessage({
                                    id: 'ContextHelp.confirmRestart.placeholder',
                                    defaultMessage: 'Yo',
                                })}
                            />
                        )}
                    </Grid>
                </Grid>

                <Grid container direction="row" alignItems="center" style={{ paddingBottom: '20px' }}>
                    <Typography variant="body1" className={classes.formName}>
                        <FormattedMessage
                            id="ContextHelp.declineRestart"
                            defaultMessage="Decline restart"
                        />
                    </Typography>
                    <Grid className={classes.formInput}>
                        {getFieldDecorator('contextHelp.declineRestart', {
                            initialValue: content.restartProgressQuestionNo,
                            preserve: true,
                        })(
                            <LocalizedPlainStringField
                                locale={contentLanguage}
                                fallbackLocale={fallbackLanguage.value}
                                placeholder={formatMessage({
                                    id: 'ContextHelp.declineRestart.placeholder',
                                    defaultMessage: 'No',
                                })}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <Grid container className={classes.itemsBox}>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="ContextHelp.launcher" defaultMessage="Launcher" />
                    </Typography>
                </Grid>

                <Grid container>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage id="ContextHelp.position" defaultMessage="Position" />
                        </Typography>

                        {metadata && (
                            <Grid id="yeself-settings-ch-position" className={classes.formInput}>
                                <FormItemSelect
                                    form={form}
                                    fullWidth={false}
                                    formItemKey="contextHelp.type"
                                    initialValue={metadata.contextHelp.container.type}
                                    optionList={CONTEXT_HELP_LAUNCHER_POSITION}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {getFieldValue('contextHelp.type') === 'attachedToElement' && (
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Grid>
                            {getFieldDecorator('contextHelp.container.location', {
                                initialValue: locationData,
                                preserve: true,
                            })(<LocationPathsForm locale={contentLanguageCode} />)}
                        </Grid>
                    </Grid>
                )}
            </Grid>

            <Grid container className={classes.itemsBox}>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="ContextHelp.placeholder" defaultMessage="Placeholder" />
                    </Typography>
                </Grid>
                <Grid container direction="row" alignItems="center" style={{ paddingBottom: '20px' }}>
                    <Typography variant="body1" className={classes.formName}>
                        <FormattedMessage
                            id="ContextHelp.searchPlaceHolder.label"
                            defaultMessage="Search placeholder"
                        />
                    </Typography>

                    <Grid className={classes.formInput}>
                        {getFieldDecorator('contextHelp.searchPlaceHolder', {
                            initialValue: content.searchInputPlaceHolder,
                            preserve: true,
                        })(
                            <LocalizedPlainStringField
                                locale={contentLanguage}
                                fallbackLocale={fallbackLanguage.value}
                                placeholder={formatMessage({
                                    id: 'ContextHelp.searchPlaceHolder.placeholder',
                                    defaultMessage: 'Search',
                                })}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <Grid container className={classes.itemsBox}>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="ContextHelp.results" defaultMessage="Results" />
                    </Typography>
                </Grid>
                <Grid container direction="row" alignItems="center" style={{ paddingBottom: '20px' }}>
                    <Typography variant="body1" className={classes.formName}>
                        <FormattedMessage
                            id="ContextHelp.noResultsFound.label"
                            defaultMessage="No results"
                        />
                    </Typography>
                    <Grid className={classes.formInput}>
                        {getFieldDecorator('contextHelp.noResultsFound', {
                            initialValue: content.noResultsFound,
                            preserve: true,
                        })(
                            <LocalizedPlainStringField
                                locale={contentLanguage}
                                fallbackLocale={fallbackLanguage.value}
                                placeholder={formatMessage({
                                    id: 'ContextHelp.noResultsFound.placeholder',
                                    defaultMessage: 'No results found',
                                })}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <Feature name={FEATURE_FLAG_NAMES.ARTICLES}>
                <Grid container className={classes.itemsBox}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.title}>
                            <FormattedMessage id="ContextHelp.articles" defaultMessage="Articles" />
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.listHeading"
                                defaultMessage="List Heading"
                            />
                        </Typography>
                        <Grid className={classes.formInput}>
                            {getFieldDecorator('contextHelp.listOfArticlesHeading', {
                                initialValue: content.listOfArticlesHeading,
                                preserve: true,
                            })(
                                <LocalizedPlainStringField
                                    locale={contentLanguage}
                                    fallbackLocale={fallbackLanguage.value}
                                    placeholder={formatMessage({
                                        id: 'ContextHelp.listOfArticlesHeading.placeholder',
                                        defaultMessage: 'List of Articles',
                                    })}
                                />
                            )}
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        <Typography variant="body1" className={classes.formName}>
                            <FormattedMessage
                                id="ContextHelp.learnMoreOfArticle.label"
                                defaultMessage="Label for complete article"
                            />
                        </Typography>
                        <Grid className={classes.formInput}>
                            {getFieldDecorator('contextHelp.learnMoreOfArticle', {
                                initialValue: content.learnMoreOfArticle,
                                preserve: true,
                            })(
                                <LocalizedPlainStringField
                                    locale={contentLanguage}
                                    fallbackLocale={fallbackLanguage.value}
                                    placeholder={formatMessage({
                                        id: 'ContextHelp.learnMoreOfArticle.placeholder',
                                        defaultMessage: 'Learn more',
                                    })}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Feature>

            <Grid container className={classes.itemsBox}>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage
                            id="ContextHelp.guidanceControl"
                            defaultMessage="Guidance control"
                        />
                    </Typography>
                </Grid>

                <Grid container direction="row" alignItems="center" style={{ paddingBottom: '20px' }}>
                    <Typography variant="body1" className={classes.formName}>
                        <FormattedMessage
                            id="ContextHelp.guidanceControl.enabled"
                            defaultMessage="Enable Guidance control"
                        />
                    </Typography>
                    <Grid id="yeself-switch-button" item>
                        {getFieldDecorator('contextHelp.content.guidanceControl.enabled', {
                            initialValue: content.guidanceControl.enabled,
                            preserve: true,
                        })(
                            <span id="yeself-switch-button">
                                <SwitchButton
                                    color="primary"
                                    value={getFieldValue('contextHelp.content.guidanceControl.enabled')}
                                />
                            </span>
                        )}
                    </Grid>
                </Grid>

                {getFieldValue('contextHelp.content.guidanceControl.enabled') && (
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        style={{ paddingBottom: '20px' }}
                    >
                        {getFieldDecorator('contextHelp.content.guidanceControl.browserPersistence', {
                            initialValue: persistanceOption,
                            preserve: true,
                        })(
                            <GuidanceControlPersistence
                                classNames={{
                                    formInput: classes.formInput,
                                    itemTextClassName: classes.formName,
                                    textField: classes.textField,
                                    menu: classes.menu,
                                    cssSelect: classes.cssSelect,
                                    cssSelectRoot: classes.cssSelectRoot,
                                    cssUnderline: classes.cssUnderline,
                                }}
                            />
                        )}

                        {getFieldDecorator(
                            'contextHelp.content.guidanceControl.flags.switchVisualGuidance.attachment',
                            {
                                initialValue: attachmentOption,
                                preserve: true,
                            }
                        )(
                            <GuidanceControlAttachmentForm
                                classNames={{
                                    formInput: classes.formInput,
                                    itemTextClassName: classes.formName,
                                    textField: classes.textField,
                                    menu: classes.menu,
                                    cssSelect: classes.cssSelect,
                                    cssSelectRoot: classes.cssSelectRoot,
                                    cssUnderline: classes.cssUnderline,
                                }}
                            />
                        )}

                        <Grid
                            container
                            direction="row"
                            alignItems="center"
                            style={{ paddingBottom: '20px' }}
                        >
                            <Typography variant="body1" className={classes.formName}>
                                <FormattedMessage
                                    id="ContextHelp.guidanceControl.labelOn"
                                    defaultMessage="Label for the guidance turned on"
                                />
                            </Typography>
                            <Grid className={classes.formInput}>
                                {getFieldDecorator(
                                    'contextHelp.content.guidanceControl.flags.switchVisualGuidance.labelOn',
                                    {
                                        initialValue: guidanceControlLabelOn,
                                        preserve: true,
                                    }
                                )(
                                    <LocalizedPlainStringField
                                        locale={contentLanguage}
                                        fallbackLocale={fallbackLanguage.value}
                                        placeholder={formatMessage({
                                            id: 'ContextHelp.guidanceControl.labelOn.placeholder',
                                            defaultMessage: 'Guidance on',
                                        })}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            direction="row"
                            alignItems="center"
                            style={{ paddingBottom: '20px' }}
                        >
                            <Typography variant="body1" className={classes.formName}>
                                <FormattedMessage
                                    id="ContextHelp.guidanceControl.labelOff"
                                    defaultMessage="Label for the guidance turned off"
                                />
                            </Typography>
                            <Grid className={classes.formInput}>
                                {getFieldDecorator(
                                    'contextHelp.content.guidanceControl.flags.switchVisualGuidance.labelOff',
                                    {
                                        initialValue: guidanceControlLabelOff,
                                        preserve: true,
                                    }
                                )(
                                    <LocalizedPlainStringField
                                        locale={contentLanguage}
                                        fallbackLocale={fallbackLanguage.value}
                                        placeholder={formatMessage({
                                            id: 'ContextHelp.guidanceControl.labelOff.placeholder',
                                            defaultMessage: 'Guidance off',
                                        })}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                )}

                <Feature name={FEATURE_FLAG_NAMES.PLAYLIST_CONSOLE_ADMIN}>
                    <ViewPlaylistsField classes={classes} form={form} />
                </Feature>
            </Grid>

            <Grid container alignItems="center">
                <Typography variant="button">
                    <Button
                        id="yeself-button"
                        variant="contained"
                        color="primary"
                        onClick={handleSaveContextHelpSettings}
                        className={classes.btn}
                    >
                        <FormattedMessage id="Buttons.save" defaultMessage="Save" />
                    </Button>
                </Typography>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(Settings));
